import { useState, useEffect } from 'react';

/**
 * Hook to detect if the user is on a mobile device
 * Only returns true if:
 * 1. User agent indicates mobile device
 * 2. Screen width <= 1024px (tablets in landscape)
 * 3. VITE_ENABLE_MOBILE feature flag is set to 'true'
 * 
 * This ensures desktop users see ZERO changes until feature is explicitly enabled
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      // Check user agent for mobile devices
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      
      // Check screen size (adjusted for DevTools testing - tablets in landscape are ~1024px)
      const isSmallScreen = window.innerWidth <= 1200; // Increased threshold for DevTools testing
      
      // Check feature flag - only enable if explicitly set to 'true'
      const featureEnabled = import.meta.env.VITE_ENABLE_MOBILE === 'true';
      
      // Only consider mobile if ALL conditions are met
      const mobileDetected = isMobileDevice && isSmallScreen && featureEnabled;
      
      setIsMobile(mobileDetected);
    };
    
    // Initial check
    checkMobile();
    
    // Listen for resize events (orientation changes, window resizing)
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};
